import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck, MailX, RotateCcw } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppPage,
  AppShell,
  AppShellMain,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type SubscriptionState = "loading" | "unsubscribed" | "subscribed" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState<SubscriptionState>("loading");
  const [errorMessage, setErrorMessage] = useState("We could not verify this link.");
  const [working, setWorking] = useState(false);

  const updateSubscription = useCallback(async (action: "unsubscribe" | "resubscribe") => {
    if (!token) {
      setState("error");
      setErrorMessage("This link is missing the email preference token.");
      return;
    }

    setWorking(true);
    const { data, error } = await supabase.functions.invoke("manage-email-subscription", {
      body: { token, action },
    });
    setWorking(false);

    if (error || !data?.ok) {
      setState("error");
      setErrorMessage(error?.message ?? data?.error ?? "We could not update your email preferences.");
      return;
    }

    setState(data.status === "subscribed" ? "subscribed" : "unsubscribed");
  }, [token]);

  useEffect(() => {
    void updateSubscription("unsubscribe");
  }, [updateSubscription]);

  const title =
    state === "subscribed"
      ? "You’re still subscribed"
      : state === "unsubscribed"
        ? "You’ve been unsubscribed"
        : state === "error"
          ? "We couldn’t update your preferences"
          : "Updating your email preferences";

  const description =
    state === "subscribed"
      ? "Kindred email updates are turned back on. You can close this page whenever you’re ready."
      : state === "unsubscribed"
        ? "Kindred email updates are now turned off for this account. If this was a mistake, you can restore them below."
        : state === "error"
          ? errorMessage
          : "Please hold on while we update your Kindred email preferences.";

  const icon = state === "subscribed" ? MailCheck : state === "unsubscribed" ? MailX : RotateCcw;
  const Icon = icon;

  return (
    <AppShell minHeight="100vh">
      <AppShellMain>
        <AppPage tag="main" justifyContent="center" maxWidth={720}>
          <div className="flex justify-center">
            <Link to="/" className="inline-flex items-center justify-center rounded-[18px] border border-border/80 bg-white px-5 py-4 shadow-card">
              <img src="/kindred-logo-full.png" alt="Kindred Community" className="h-10 w-auto sm:h-11" />
            </Link>
          </div>

          <AppCard>
            <AppCardBody alignItems="center" py="$xl" gap="$md">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon size={22} />
              </div>

              <AppText
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$xl"
                lineHeight="$xl"
                color="$heading"
                textAlign="center"
              >
                {title}
              </AppText>

              <AppText color="$colorMuted" lineHeight="$md" textAlign="center">
                {description}
              </AppText>

              {state === "unsubscribed" ? (
                <AppButton
                  tone="outline"
                  onPress={() => void updateSubscription("resubscribe")}
                  disabled={working}
                >
                  {working ? "Updating..." : "Turn updates back on"}
                </AppButton>
              ) : state === "subscribed" ? (
                <AppButton
                  tone="outline"
                  onPress={() => void updateSubscription("unsubscribe")}
                  disabled={working}
                >
                  {working ? "Updating..." : "Turn updates off again"}
                </AppButton>
              ) : null}

              <Link to="/">
                <AppButton tone="primary">Return to Kindred</AppButton>
              </Link>
            </AppCardBody>
          </AppCard>
        </AppPage>
      </AppShellMain>
    </AppShell>
  );
};

export default Unsubscribe;
